import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, map, catchError, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthenticationService } from './authentication.service';

export interface MeResponse {
  user: {
    id: number | string;
    userId?: string;
    displayName: string;
    username: string;
    name: string;
    email: string;
    departmentId?: string | null;
  };
  roles: string[];
  permissions: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthenticationService);
  private readonly API_URL = `${environment.apiUrl}/auth/me`;

  readonly permissions = signal<string[]>([]);
  readonly roles = signal<string[]>([]);

  readonly isSystemAdmin = computed(() => this.hasRole('SYSTEM_ADMIN') || this.can('*'));
  readonly isAdmin = computed(() => this.isSystemAdmin() || this.hasRole('ADMIN'));
  readonly isDepartmentHead = computed(() => this.hasRole('DEPARTMENT_HEAD') || this.isAdmin());
  /** True when the user is EMPLOYEE or DEPARTMENT_HEAD (not admin) — must be scoped to their department */
  readonly isDeptScopedRole = computed(() => !this.isAdmin() && (this.hasRole('EMPLOYEE') || this.hasRole('DEPARTMENT_HEAD')));
  /** True for admin/system-admin — can see all departments */
  readonly isGlobalAdmin = computed(() => this.isAdmin());

  constructor() {
    if (this.authService.getAccessToken()) {
      this.loadUserPermissions().subscribe();
    }
  }

  loadUserPermissions(): Observable<MeResponse | null> {
    if (!this.authService.getAccessToken()) {
      return of(null);
    }

    return this.http.get<any>(this.API_URL).pipe(
      map((res) => (res.data ? res.data : res)),
      tap((data: MeResponse) => {
        if (data?.permissions) {
          this.permissions.set(data.permissions);
        }
        if (data?.roles) {
          this.roles.set(data.roles);
        }
        if (data?.user?.departmentId) {
          const user = this.authService.currentUser();
          if (user && String(user.departmentId) !== String(data.user.departmentId)) {
            const updated = { ...user, departmentId: String(data.user.departmentId) };
            this.authService.currentUser.set(updated);
            sessionStorage.setItem(`${this.authService.APP_NAMESPACE}_user`, JSON.stringify(updated));
            localStorage.setItem(`${this.authService.APP_NAMESPACE}_user`, JSON.stringify(updated));
          }
        }
      }),
      catchError(() => of(null)),
    );
  }

  can(permission: string): boolean {
    const list = this.permissions();
    if (list.includes('*') || this.roles().includes('SYSTEM_ADMIN')) {
      return true;
    }
    if (list.includes(permission)) {
      return true;
    }

    // Support section wildcard matching e.g. ticket.* covers ticket.view
    const parts = permission.split('.');
    if (parts.length > 1 && list.includes(`${parts[0]}.*`)) {
      return true;
    }

    return false;
  }

  canAny(...permissions: string[]): boolean {
    return permissions.some((p) => this.can(p));
  }

  canAll(...permissions: string[]): boolean {
    return permissions.every((p) => this.can(p));
  }

  hasRole(role: string): boolean {
    const list = this.roles();
    if (list.length > 0) {
      return list.includes(role);
    }
    const user = this.authService.currentUser();
    const storedRoles: string[] = (user as any)?.roles || (user?.role ? [user.role] : []);
    if (storedRoles.length > 0) {
      const upperTarget = role.toUpperCase();
      return storedRoles.some(
        (r) =>
          r.toUpperCase() === upperTarget ||
          r.toUpperCase().replace(/\s+/g, '_') === upperTarget ||
          (upperTarget === 'EMPLOYEE' && r.toLowerCase() === 'employee') ||
          (upperTarget === 'DEPARTMENT_HEAD' && r.toLowerCase().includes('department head')) ||
          (upperTarget === 'ADMIN' && (r.toLowerCase() === 'admin' || r.toLowerCase() === 'administrator')) ||
          (upperTarget === 'SYSTEM_ADMIN' && (r.toLowerCase().includes('system admin') || r.toLowerCase() === 'system_admin'))
      );
    }
    return false;
  }

  refreshPermissions(): void {
    this.loadUserPermissions().subscribe();
  }
}
